import { useEffect } from 'react';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import { useToaster } from '~/modules/root/context/ToasterContext';
import {
    getContentEditorLink,
    getRepositionThumbLink,
} from '~/modules/root/util/item-link-helpers';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { DELETE_DIRECTORY_ITEM } from '~/routes/remix-api.directory.delete-directory-item';
import type { action as deleteAction } from '~/routes/remix-api.directory.delete-directory-item';
import { useToggleHomePin } from '~/modules/home/hook/use-toggle-home-pin';
import { useToggleFeaturedBrandPin } from '~/modules/home/hook/use-toggle-featured-brand-pin';

interface UseTileAdminActionsProps {
    item: {
        id: string;
        link: string;
        professionalLink: string;
        pinned?: boolean;
        featuredBrandPinned?: boolean;
        productsAllowed?: boolean;
    };
    itemType: 'products' | 'projects' | 'professionals' | 'articles';
    onToggleFeature?: () => void;
    onRemoveFromLibrary?: () => void;
}

/**
 * Hook to generate the admin actions for a tile.
 */
export const useTileAdminActions = (input: UseTileAdminActionsProps) => {
    const { item, itemType, onToggleFeature, onRemoveFromLibrary } = input;
    const deleteFetcher = useAppFetcher<{ deleteDirectoryItem?: boolean }>();
    const { toast } = useToaster();
    const { toggleHomePin } = useToggleHomePin(
        (error) => {
            if (error.Message) {
                toast(error.Message, { type: 'error' });
            }
        },
        (pinned) => {
            toast(pinned ? 'Pinned to homepage.' : 'Unpinned from homepage.', {
                type: 'success',
            });
        },
    );
    const { toggleFeaturedBrandPin } = useToggleFeaturedBrandPin(
        (error) => {
            if (error.Message) {
                toast(error.Message, { type: 'error' });
            }
        },
        (pinned) => {
            toast(
                pinned
                    ? 'Brand pinned to Featured Brands.'
                    : 'Brand unpinned from Featured Brands.',
                { type: 'success' },
            );
        },
    );

    useEffect(() => {
        if (
            fetcherIsDone(deleteFetcher) &&
            deleteFetcher.data.deleteDirectoryItem === true
        ) {
            toast(
                `${itemType.charAt(0).toUpperCase() + itemType.slice(1)} deleted, please allow a few minutes for results to be updated.`,
                { type: 'success' },
            );
        }
    }, [deleteFetcher, toast, itemType]);

    const onEdit = () => {
        const url = getContentEditorLink(
            itemType,
            item.link,
            item.professionalLink,
        );
        if (url && typeof window !== 'undefined') {
            window.open(url, '_blank')?.focus();
        }
    };

    const onRepositionImage = () => {
        const url = getRepositionThumbLink(
            itemType,
            item.link,
            item.professionalLink,
        );
        if (url && typeof window !== 'undefined') {
            window.open(url, '_blank')?.focus();
        }
    };

    // disable delete for professionals
    const onDelete =
        itemType === 'professionals'
            ? undefined
            : () => {
                  deleteFetcher.submit(
                      {
                          ID: item.id.toString(),
                      },
                      {
                          method: 'post',
                          action: DELETE_DIRECTORY_ITEM,
                      },
                  );
              };

    const onTogglePin = toggleHomePin
        ? () => toggleHomePin(Number(item.id), !item.pinned)
        : undefined;

    // Only show Featured Brand Pin for suppliers (professionals allowed to
    // have products) — the pinned set drives the curated default product
    // list, so the action stays on the 5.0 browse too.
    const onToggleFeaturedBrandPin =
        toggleFeaturedBrandPin &&
        itemType === 'professionals' &&
        item.productsAllowed
            ? () =>
                  toggleFeaturedBrandPin(
                      Number(item.id),
                      !item.featuredBrandPinned,
                  )
            : undefined;

    // Remove From Library is only for project and product tiles
    const removeFromLibraryAction =
        itemType === 'projects' || itemType === 'products'
            ? onRemoveFromLibrary
            : undefined;

    return {
        onEdit,
        onRepositionImage,
        onDelete,
        onTogglePin,
        onToggleFeature,
        onToggleFeaturedBrandPin,
        onRemoveFromLibrary: removeFromLibraryAction,
    };
};
